import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    withCredentials: true,
});

let refreshPromise = null;
api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const url = original.url || "";
        if (status === 401 && !original._retry && !url.startsWith("/auth/")) {
            original._retry = true;
            try {
                refreshPromise = refreshPromise || api.post("/auth/refresh");
                await refreshPromise;
                refreshPromise = null;
                return api(original);
            } catch (e) {
                refreshPromise = null;
            }
        }
        return Promise.reject(error);
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function apiError(e, fallback = "Something went wrong. Please try again.") {
    return formatApiErrorDetail(e?.response?.data?.detail) || e?.message || fallback;
}

const obsHeaders = (t) => (t ? { headers: { "X-OBS-Token": t } } : undefined);

// ---- auth ----
export const registerUser = (data) => api.post("/auth/register", data).then((r) => r.data);
export const forgotPassword = (email) =>
    api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (email, code, new_password) =>
    api.post("/auth/reset-password", { email, code, new_password }).then((r) => r.data);
export const loginUser = (data) => api.post("/auth/login", data).then((r) => r.data);
export const googleSession = (session_id) =>
    api.post("/auth/google/session", { session_id }).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const logoutUser = () => api.post("/auth/logout");
export const updateProfile = (data) => api.put("/auth/profile", data).then((r) => r.data);
export const changePassword = (data) => api.put("/auth/password", data).then((r) => r.data);
export const regenerateObsToken = () =>
    api.post("/auth/regenerate-obs-token").then((r) => r.data);

// ---- billing / dashboard ----
export const getPackages = () => api.get("/packages").then((r) => r.data);
export const createCheckout = (package_id) =>
    api.post("/billing/checkout", { package_id }).then((r) => r.data);
export const verifyPayment = (transaction_id, tx_ref) =>
    api.post("/billing/verify", { transaction_id, tx_ref }).then((r) => r.data);
export const getTransactions = () => api.get("/billing/transactions").then((r) => r.data);
export const getDashboardStats = () => api.get("/dashboard/stats").then((r) => r.data);

// ---- support ----
export const contactSupport = (data) => api.post("/support/contact", data).then((r) => r.data);
export const getTickets = () => api.get("/support/tickets").then((r) => r.data);
export const getSupportUnread = () => api.get("/support/unread").then((r) => r.data);
export const markSupportSeen = () => api.post("/support/seen").then((r) => r.data);

// ---- streaming meter ----
export const streamStart = (source = "studio", obsToken = null, speed = "standard") =>
    api.post("/stream/start", { source, speed }, obsHeaders(obsToken)).then((r) => r.data);
export const streamHeartbeat = (stream_id, seconds, obsToken = null) =>
    api.post("/stream/heartbeat", { stream_id, seconds }, obsHeaders(obsToken)).then((r) => r.data);
export const streamStop = (stream_id, obsToken = null) =>
    api.post("/stream/stop", { stream_id }, obsHeaders(obsToken)).then((r) => r.data);

// ---- OBS style sync ----
export const getObsConfig = (token) =>
    api.get("/obs/config", { params: { token } }).then((r) => r.data);
export const pushObsStyle = (prompt) => api.put("/obs/style", { prompt }).then((r) => r.data);

// ---- admin ----
export const getAdminStats = () => api.get("/admin/stats").then((r) => r.data);
export const getAdminUsers = (params) => api.get("/admin/users", { params }).then((r) => r.data);
export const getAdminUser = (id) => api.get(`/admin/users/${id}`).then((r) => r.data);
export const updateAdminUser = (id, data) => api.put(`/admin/users/${id}`, data).then((r) => r.data);
export const getAdminBank = () => api.get("/admin/bank").then((r) => r.data);
export const getBankLedger = (limit = 50) =>
    api.get("/admin/bank/ledger", { params: { limit } }).then((r) => r.data);
export const getBankUsageDaily = (days = 14) =>
    api.get("/admin/bank/usage-daily", { params: { days } }).then((r) => r.data);
export const adminBankTopup = (amount, note = "") =>
    api.post("/admin/bank/topup", { amount, note }).then((r) => r.data);
export const adminBankWithdraw = (amount, note = "") =>
    api.post("/admin/bank/withdraw", { amount, note }).then((r) => r.data);
export const adminAdjustCredits = (id, amount, note) =>
    api.post(`/admin/users/${id}/credits`, { amount, note }).then((r) => r.data);
export const deleteAdminUser = (id) => api.delete(`/admin/users/${id}`).then((r) => r.data);
export const getAdminTickets = () => api.get("/admin/tickets").then((r) => r.data);
export const updateAdminTicket = (id, status) =>
    api.put(`/admin/tickets/${id}`, { status }).then((r) => r.data);
export const replyAdminTicket = (id, message) =>
    api.post(`/admin/tickets/${id}/reply`, { message }).then((r) => r.data);
export const replyMyTicket = (id, message) =>
    api.post(`/support/tickets/${id}/reply`, { message }).then((r) => r.data);
export const getAdminUnread = () => api.get("/admin/unread").then((r) => r.data);
export const markAdminSeen = () => api.post("/admin/seen").then((r) => r.data);
